import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Search, Filter, MessageSquare, Send, Clock, CheckCircle,
  AlertCircle, HelpCircle, FileText, RefreshCw, X, ChevronDown, User,
  StickyNote, Plus
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const TICKET_TYPES = {
  BUG: { label: 'Bug', icon: AlertCircle, color: '#f87171' },
  ACCOUNT: { label: 'Account', icon: HelpCircle, color: '#60a5fa' },
  BILLING: { label: 'Billing', icon: FileText, color: '#34d399' },
  FEATURE: { label: 'Feature', icon: Plus, color: '#a78bfa' },
  OTHER: { label: 'Other', icon: MessageSquare, color: '#94a3b8' }
};

const STATUS_CONFIG = {
  OPEN: { label: 'Open', color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
  IN_PROGRESS: { label: 'In Progress', color: '#fbbf24', bg: 'rgba(251,191,36,0.1)' },
  WAITING_ON_USER: { label: 'Waiting', color: '#f97316', bg: 'rgba(249,115,22,0.1)' },
  RESOLVED: { label: 'Resolved', color: '#34d399', bg: 'rgba(52,211,153,0.1)' },
  CLOSED: { label: 'Closed', color: '#71717a', bg: 'rgba(113,113,122,0.1)' }
};

const PRIORITY_CONFIG = {
  LOW: { label: 'Low', color: '#71717a' },
  MEDIUM: { label: 'Medium', color: '#fbbf24' },
  HIGH: { label: 'High', color: '#f87171' }
};

const SimsPage = () => {
  const navigate = useNavigate();
  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token') || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Reply/Update state
  const [replyText, setReplyText] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [sending, setSending] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  
  // Admin login
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await axios.post(`${API_BASE}/admin/login`, { password });
      if (response.data.success) {
        const token = response.data.token;
        setAdminToken(token);
        localStorage.setItem('admin_token', token);
        setIsAuthenticated(true);
        toast.success('Admin access granted');
      }
    } catch (error) {
      setLoginError('Invalid admin password');
    }
  };
  
  // Verify token on mount
  useEffect(() => {
    if (adminToken) {
      setIsAuthenticated(true);
    }
  }, [adminToken]);
  
  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    if (!isAuthenticated || !adminToken) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('pageSize', 20);
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('type', typeFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API_BASE}/admin/sims/tickets?${params}`, {
        headers: { 'x-admin-secret': adminToken }
      });
      setTickets(response.data.tickets || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('admin_token');
        toast.error('Admin session expired');
      } else {
        toast.error('Failed to load tickets');
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, adminToken, page, statusFilter, typeFilter, searchQuery]);
  
  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);
  
  // Fetch ticket detail
  const fetchTicketDetail = async (ticketId) => {
    try {
      const response = await axios.get(`${API_BASE}/admin/sims/tickets/${ticketId}`, {
        headers: { 'x-admin-secret': adminToken }
      });
      setSelectedTicket(response.data);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };
  
  // Reply to ticket
  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedTicket) return;
    
    setSending(true);
    try {
      await axios.post(`${API_BASE}/admin/sims/tickets/${selectedTicket.id}/reply`, {
        message: replyText
      }, { headers: { 'x-admin-secret': adminToken } });
      
      toast.success('Reply sent');
      setReplyText('');
      fetchTicketDetail(selectedTicket.id);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };
  
  // Add internal note
  const handleAddNote = async () => {
    if (!internalNote.trim() || !selectedTicket) return;
    
    try {
      await axios.post(`${API_BASE}/admin/sims/tickets/${selectedTicket.id}/internal-note`, {
        note: internalNote
      }, { headers: { 'x-admin-secret': adminToken } });
      
      toast.success('Note added');
      setInternalNote('');
      fetchTicketDetail(selectedTicket.id);
    } catch (error) {
      toast.error('Failed to add note');
    }
  };
  
  // Update ticket status/priority
  const handleUpdateTicket = async (updates) => {
    if (!selectedTicket) return;
    
    try {
      await axios.patch(`${API_BASE}/admin/sims/tickets/${selectedTicket.id}`, updates, {
        headers: { 'x-admin-secret': adminToken }
      });
      
      toast.success('Ticket updated');
      fetchTicketDetail(selectedTicket.id);
      fetchTickets();
      setShowStatusMenu(false);
      setShowPriorityMenu(false);
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Admin Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#09090b' }}>
        <div 
          className="w-full max-w-md p-8 rounded-xl"
          style={{
            backgroundColor: '#18181b',
            border: '1px solid rgba(255,255,255,0.08)'
          }}
        >
          <h1 className="text-2xl font-bold text-center mb-2" style={{ color: '#f4f4f5' }}>
            SIMs Admin
          </h1>
          <p className="text-center mb-6" style={{ color: '#71717a' }}>
            Internal Ticketing System
          </p>
          
          <form onSubmit={handleAdminLogin} className="space-y-4">
            <div>
              <label className="block text-sm mb-1.5" style={{ color: '#a1a1aa' }}>Admin Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{
                  backgroundColor: '#27272a',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#f4f4f5'
                }}
                placeholder="Enter admin password"
                required
                data-testid="admin-password-input"
              />
            </div>
            
            {loginError && (
              <p className="text-sm text-red-400">{loginError}</p>
            )}
            
            <Button type="submit" className="w-full" data-testid="admin-login-btn">
              Access SIMs
            </Button>
          </form>
          
          <button
            onClick={() => navigate('/app')}
            className="w-full mt-4 text-sm"
            style={{ color: '#71717a' }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#09090b' }}>
      {/* Header */}
      <div className="border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/app')}
                className="p-2 rounded-lg transition-colors"
                style={{ color: '#71717a' }}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold" style={{ color: '#f4f4f5' }}>
                  SIMs Dashboard
                </h1>
                <p className="text-sm" style={{ color: '#71717a' }}>
                  {total} total ticket{total !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchTickets}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  localStorage.removeItem('admin_token');
                  setIsAuthenticated(false);
                }}
              >
                Logout
              </Button>
            </div>
          </div>
          
          {/* Filters */}
          <div className="flex items-center gap-3 mt-4">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#52525b' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tickets..."
                className="w-full pl-10 pr-4 py-2 rounded-lg text-sm"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#f4f4f5'
                }}
                data-testid="search-input"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-lg text-sm appearance-none cursor-pointer"
              style={{
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#f4f4f5'
              }}
              data-testid="status-filter"
            >
              <option value="" style={{ backgroundColor: '#27272a', color: '#f4f4f5' }}>All Status</option>
              {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                <option key={key} value={key} style={{ backgroundColor: '#27272a', color: '#f4f4f5' }}>
                  {val.label}
                </option>
              ))}
            </select>
            
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 rounded-lg text-sm appearance-none cursor-pointer"
              style={{
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#f4f4f5'
              }}
              data-testid="type-filter"
            >
              <option value="" style={{ backgroundColor: '#27272a', color: '#f4f4f5' }}>All Types</option>
              {Object.entries(TICKET_TYPES).map(([key, val]) => (
                <option key={key} value={key} style={{ backgroundColor: '#27272a', color: '#f4f4f5' }}>
                  {val.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Ticket List */}
          <div className="w-1/2">
            {loading ? (
              <div className="text-center py-12" style={{ color: '#71717a' }}>
                Loading tickets...
              </div>
            ) : tickets.length === 0 ? (
              <div 
                className="text-center py-12 rounded-xl"
                style={{ 
                  backgroundColor: 'rgba(255,255,255,0.02)',
                  border: '1px dashed rgba(255,255,255,0.1)'
                }}
              >
                <MessageSquare className="w-12 h-12 mx-auto mb-4" style={{ color: '#3f3f46' }} />
                <h3 className="text-lg font-medium mb-2" style={{ color: '#a1a1aa' }}>
                  No tickets found
                </h3>
              </div>
            ) : (
              <div className="space-y-2">
                {tickets.map((ticket) => {
                  const typeInfo = TICKET_TYPES[ticket.type] || TICKET_TYPES.OTHER;
                  const statusInfo = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.OPEN;
                  const priorityInfo = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.MEDIUM;
                  const TypeIcon = typeInfo.icon;
                  
                  return (
                    <button
                      key={ticket.id}
                      onClick={() => fetchTicketDetail(ticket.id)}
                      className={`w-full p-3 rounded-lg flex items-center gap-3 text-left transition-colors ${selectedTicket?.id === ticket.id ? 'ring-1 ring-blue-500' : ''}`}
                      style={{
                        backgroundColor: selectedTicket?.id === ticket.id ? 'rgba(59,130,246,0.1)' : 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.06)'
                      }}
                      data-testid={`ticket-row-${ticket.ticketNumber}`}
                    >
                      <div 
                        className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                        style={{
                          backgroundColor: `${typeInfo.color}15`,
                          color: typeInfo.color
                        }}
                      >
                        <TypeIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono" style={{ color: '#71717a' }}>
                            {ticket.ticketNumber}
                          </span>
                          <span 
                            className="text-xs px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: statusInfo.bg, color: statusInfo.color }}
                          >
                            {statusInfo.label}
                          </span>
                          <span 
                            className="text-xs"
                            style={{ color: priorityInfo.color }}
                          >
                            {priorityInfo.label}
                          </span>
                        </div>
                        <p className="text-sm truncate mt-0.5" style={{ color: '#f4f4f5' }}>
                          {ticket.subject}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: '#52525b' }}>
                          {ticket.userEmail} • {formatDate(ticket.lastMessageAt || ticket.updatedAt)}
                        </p>
                      </div>
                    </button>
                  );
                })}
                
                {/* Pagination */}
                {total > 20 && (
                  <div className="flex justify-center gap-2 pt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <span className="px-3 py-2 text-sm" style={{ color: '#71717a' }}>
                      Page {page} of {Math.ceil(total / 20)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= Math.ceil(total / 20)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Ticket Detail */}
          <div className="w-1/2">
            {selectedTicket ? (
              <div 
                className="rounded-xl h-full flex flex-col"
                style={{
                  backgroundColor: '#18181b',
                  border: '1px solid rgba(255,255,255,0.08)'
                }}
              >
                {/* Ticket Header */}
                <div className="p-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-mono text-sm" style={{ color: '#71717a' }}>
                      {selectedTicket.ticketNumber}
                    </span>
                    <button onClick={() => setSelectedTicket(null)} style={{ color: '#71717a' }}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <h3 className="font-medium" style={{ color: '#f4f4f5' }}>
                    {selectedTicket.subject}
                  </h3>
                  <div className="flex items-center gap-2 mt-2">
                    <User className="w-3 h-3" style={{ color: '#52525b' }} />
                    <span className="text-sm" style={{ color: '#a1a1aa' }}>
                      {selectedTicket.userEmail}
                    </span>
                  </div>
                  
                  {/* Status & Priority Controls */}
                  <div className="flex items-center gap-2 mt-3">
                    {/* Status Dropdown */}
                    <div className="relative">
                      <button
                        onClick={() => setShowStatusMenu(!showStatusMenu)}
                        className="flex items-center gap-1 px-2 py-1 rounded text-xs"
                        style={{
                          backgroundColor: STATUS_CONFIG[selectedTicket.status]?.bg,
                          color: STATUS_CONFIG[selectedTicket.status]?.color
                        }}
                      >
                        {STATUS_CONFIG[selectedTicket.status]?.label}
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      {showStatusMenu && (
                        <div 
                          className="absolute top-full left-0 mt-1 rounded-lg py-1 z-10"
                          style={{
                            backgroundColor: '#27272a',
                            border: '1px solid rgba(255,255,255,0.1)',
                            minWidth: '120px'
                          }}
                        >
                          {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                            <button
                              key={key}
                              onClick={() => handleUpdateTicket({ status: key })}
                              className="w-full px-3 py-1.5 text-left text-sm hover:bg-white/5"
                              style={{ color: val.color }}
                            >
                              {val.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Priority Dropdown */}
                    <div className="relative">
                      <button
                        onClick={() => setShowPriorityMenu(!showPriorityMenu)}
                        className="flex items-center gap-1 px-2 py-1 rounded text-xs"
                        style={{
                          backgroundColor: 'rgba(255,255,255,0.05)',
                          color: PRIORITY_CONFIG[selectedTicket.priority]?.color
                        }}
                      >
                        {PRIORITY_CONFIG[selectedTicket.priority]?.label}
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      {showPriorityMenu && (
                        <div 
                          className="absolute top-full left-0 mt-1 rounded-lg py-1 z-10"
                          style={{
                            backgroundColor: '#27272a',
                            border: '1px solid rgba(255,255,255,0.1)',
                            minWidth: '100px'
                          }}
                        >
                          {Object.entries(PRIORITY_CONFIG).map(([key, val]) => (
                            <button
                              key={key}
                              onClick={() => handleUpdateTicket({ priority: key })}
                              className="w-full px-3 py-1.5 text-left text-sm hover:bg-white/5"
                              style={{ color: val.color }}
                            >
                              {val.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: '300px' }}>
                  {selectedTicket.messages?.map((msg, idx) => (
                    <div 
                      key={idx}
                      className={`flex ${msg.sender === 'ADMIN' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div 
                        className="max-w-[85%] p-2.5 rounded-lg"
                        style={{
                          backgroundColor: msg.sender === 'ADMIN' 
                            ? 'rgba(59,130,246,0.15)' 
                            : 'rgba(255,255,255,0.05)'
                        }}
                      >
                        <p className="text-sm whitespace-pre-wrap" style={{ color: '#f4f4f5' }}>
                          {msg.message}
                        </p>
                        <p className="text-xs mt-1" style={{ color: '#52525b' }}>
                          {formatDate(msg.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Internal Notes */}
                {selectedTicket.internalNotes?.length > 0 && (
                  <div className="px-4 py-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                    <p className="text-xs font-medium mb-2 flex items-center gap-1" style={{ color: '#fbbf24' }}>
                      <StickyNote className="w-3 h-3" />
                      Internal Notes
                    </p>
                    <div className="space-y-1">
                      {selectedTicket.internalNotes.map((note, idx) => (
                        <div key={idx} className="text-xs p-2 rounded" style={{ backgroundColor: 'rgba(251,191,36,0.1)' }}>
                          <p style={{ color: '#f4f4f5' }}>{note.note}</p>
                          <p className="mt-1" style={{ color: '#52525b' }}>{formatDate(note.createdAt)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Reply & Notes Form */}
                <div className="p-4 border-t space-y-3" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                  {/* Reply */}
                  <form onSubmit={handleReply} className="flex gap-2">
                    <input
                      type="text"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Reply to user..."
                      className="flex-1 px-3 py-2 rounded-lg text-sm"
                      style={{
                        backgroundColor: '#27272a',
                        border: '1px solid rgba(255,255,255,0.08)',
                        color: '#f4f4f5'
                      }}
                      data-testid="admin-reply-input"
                    />
                    <Button type="submit" disabled={sending || !replyText.trim()} size="sm">
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                  
                  {/* Internal Note */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={internalNote}
                      onChange={(e) => setInternalNote(e.target.value)}
                      placeholder="Add internal note..."
                      className="flex-1 px-3 py-2 rounded-lg text-sm"
                      style={{
                        backgroundColor: 'rgba(251,191,36,0.05)',
                        border: '1px solid rgba(251,191,36,0.2)',
                        color: '#f4f4f5'
                      }}
                      data-testid="internal-note-input"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={handleAddNote}
                      disabled={!internalNote.trim()}
                      size="sm"
                    >
                      <StickyNote className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div 
                className="h-full rounded-xl flex items-center justify-center"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.02)',
                  border: '1px dashed rgba(255,255,255,0.1)',
                  minHeight: '400px'
                }}
              >
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4" style={{ color: '#3f3f46' }} />
                  <p style={{ color: '#71717a' }}>Select a ticket to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimsPage;
